import React, { useState, useEffect, useRef, useCallback } from 'react';
import { money } from './IntakeSteps';

const W = 600;
const H = 240;
const PAD = { t: 18, r: 14, b: 22, l: 14 };

// Four chapters of the journey. Each ends at a point index in the real curve.
// `hold` = wait for the member to tap before continuing (the emotional beat).
// Durations are deliberately SLOW. Jon's note: humans process this far slower than we do,
// and the whole point is to let the dip be felt rather than glimpsed.
const CHAPTERS = [
  { key: 'climb', ms: 10400, hold: false },
  { key: 'fall', ms: 8400, hold: true },
  { key: 'back', ms: 6000, hold: false },
  { key: 'on', ms: 8800, hold: false },
];
const BETWEEN_MS = 1200;

const reduceMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Callout = ({ left, top, place, title, value, tone, testid }) => (
  <div
    className={`ik-callout is-${place} ${tone ? `is-${tone}` : ''}`}
    style={{ left: `${Math.min(Math.max(left, 7), 93)}%`, top: `${top}%` }}
    data-testid={testid}
  >
    <div className="ik-callout-box">
      <div className="ik-callout-title">{title}</div>
      <div className="ik-callout-value">{value}</div>
    </div>
  </div>
);

const IntakeJourney = ({ journey, allocation, onSeen }) => {
  const { points, dip } = journey;
  const n = points.length;
  const stops = [dip.peak_i, dip.trough_i, dip.recovery_i, n - 1];

  const [ch, setCh] = useState(0);
  const [i, setI] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const [done, setDone] = useState(false);
  const raf = useRef(null);
  const timer = useRef(null);
  const alive = useRef(true);
  const reduced = useRef(reduceMotion());

  // Clamp the draw index. Guarantees points[k] is always defined even if a stray async
  // update lands after a navigation, which is how the earlier NaN crash happened.
  const safeI = Number.isFinite(i) ? Math.min(Math.max(i, 0), n - 1) : 0;

  const vals = points.map((p) => p.v);
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const x = (k) => PAD.l + (k / (n - 1)) * (W - PAD.l - PAD.r);
  const y = (v) => PAD.t + (1 - (v - lo) / (hi - lo)) * (H - PAD.t - PAD.b);

  const finish = useCallback(() => {
    setI(n - 1);
    setCh(CHAPTERS.length);
    setDone(true);
    setWaiting(false);
    onSeen?.();
  }, [n, onSeen]);

  // NOTE: StrictMode double-invokes effects on mount (mount -> cleanup -> mount), so
  // `alive` MUST be re-armed here. Setting it only in the cleanup permanently kills the
  // animation on the second mount.
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (raf.current) cancelAnimationFrame(raf.current);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    if (reduced.current) finish();
  }, [finish]);

  useEffect(() => {
    if (reduced.current || done || waiting || ch >= CHAPTERS.length) return;
    const from = ch === 0 ? 0 : stops[ch - 1];
    const to = stops[ch];
    const dur = CHAPTERS[ch].ms;
    const t0 = performance.now();
    const tick = (now) => {
      if (!alive.current) return;
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 2);
      setI(Math.round(from + (to - from) * eased));
      if (p < 1) {
        raf.current = requestAnimationFrame(tick);
      } else if (CHAPTERS[ch].hold) {
        setWaiting(true);
      } else if (ch === CHAPTERS.length - 1) {
        setDone(true);
        onSeen?.();
      } else {
        timer.current = setTimeout(() => {
          if (alive.current) setCh((c) => Math.min(c + 1, CHAPTERS.length - 1));
        }, BETWEEN_MS);
      }
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [ch, waiting, done]);

  const replay = () => {
    if (timer.current) clearTimeout(timer.current);
    if (raf.current) cancelAnimationFrame(raf.current);
    setDone(false);
    setWaiting(false);
    setI(0);
    setCh(0);
  };

  const shown = points.slice(0, Math.max(2, safeI + 1));
  const line = shown.map((p, k) => `${k === 0 ? 'M' : 'L'}${x(k)},${y(p.v)}`).join(' ');
  const area = `${line} L${x(shown.length - 1)},${y(lo)} L${x(0)},${y(lo)} Z`;

  const val = (k) => allocation * points[Math.min(Math.max(k, 0), n - 1)].v;
  const cur = val(safeI);
  const atTrough = safeI >= dip.trough_i;
  const pastPeak = safeI >= dip.peak_i;

  // The emotional window: peak -> recovery, shaded once the fall has begun.
  const bandL = x(dip.peak_i);
  const bandR = x(Math.min(safeI, dip.recovery_i));

  // Plain-language time: "480 days" means nothing to a reader, "about 16 months" does.
  const inMonths = (d) => {
    const m = Math.round(d / 30.44);
    return m >= 24
      ? `about ${Math.round(m / 12)} years`
      : `about ${m} month${m === 1 ? '' : 's'}`;
  };

  const NARRATION = [
    {
      title: 'The first few years.',
      body: `You put ${money(allocation)} to work ten years ago. It climbs. Not in a straight
        line, there are small dips along the way, but the direction is up. This part is easy
        to hold.`,
    },
    {
      title: 'And then this happens.',
      body: `Your money reached its highest point, ${money(val(dip.peak_i))}, and then it
        started to fall. From that high point it took ${dip.days_down} days,
        ${inMonths(dip.days_down)}, to reach the bottom of the fall. At the bottom it was
        worth ${money(val(dip.trough_i))}. That is ${money(val(dip.peak_i) - val(dip.trough_i))}
        less than at the top, a fall of ${Math.abs(dip.depth_pct)}%. Nothing is broken and
        nothing has gone wrong. This is what the journey costs, and this is the moment that
        decides everything.`,
    },
    {
      title: 'Now watch what happens if you stay.',
      body: `From the bottom it took ${dip.days_back} days, ${inMonths(dip.days_back)}, to climb
        all the way back. ${money(val(dip.trough_i))} became ${money(val(dip.recovery_i))},
        a gain of ${dip.gain_out_pct}% from the bottom, which put you back above the high
        point you fell from.`,
    },
    {
      title: 'And then it keeps going.',
      body: `Ten years, start to finish: ${money(allocation)} becomes ${money(val(n - 1))}.
        The fall you just watched is already included in that final number. So are
        ${journey.dips_over_5pct - 1} other falls of 5% or more.`,
    },
  ];

  const narr = NARRATION[Math.min(ch, NARRATION.length - 1)];

  return (
    <div className="ik-journey" data-testid="intake-journey">
      <div className="ik-journey-head">
        <div>
          <div className="ik-journey-key">Your {money(allocation)} over ten years</div>
          <div className="ik-journey-val" data-testid="intake-journey-value">{money(cur)}</div>
        </div>
        {safeI > dip.peak_i && (
          <div className={`ik-journey-dd ${
            safeI < dip.recovery_i ? 'is-hot' : 'is-ok'}`} data-testid="intake-journey-dd">
            {safeI < dip.recovery_i ? (
              <>
                <span className="ik-journey-dd-lead">You've dropped</span>
                <span className="ik-journey-dd-num">
                  {(((cur - val(dip.peak_i)) / val(dip.peak_i)) * 100).toFixed(1)}%
                </span>
              </>
            ) : (
              <>
                <span className="ik-journey-dd-lead">Recovered</span>
                <span className="ik-journey-dd-num is-word">back to even</span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="ik-journey-plot">
        <svg viewBox={`0 0 ${W} ${H}`} className="ik-journey-svg" preserveAspectRatio="none"
          role="img" aria-label="Ten year journey of the system, showing a drawdown and recovery">
        <defs>
          <linearGradient id="ikFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.30" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
          </linearGradient>
        </defs>

        {pastPeak && bandR > bandL && (
          <rect x={bandL} y={PAD.t} width={bandR - bandL} height={H - PAD.t - PAD.b}
            className="ik-journey-band" />
        )}

        <path d={area} fill="url(#ikFill)" />
        <path d={line} className="ik-journey-line" />

        {pastPeak && (
          <circle cx={x(dip.peak_i)} cy={y(points[dip.peak_i].v)} r="3.5"
            className="ik-journey-dot" />
        )}
        {atTrough && (
          <circle cx={x(dip.trough_i)} cy={y(points[dip.trough_i].v)} r="4.5"
            className="ik-journey-dot is-trough" />
        )}
        {safeI >= dip.recovery_i && (
          <circle cx={x(dip.recovery_i)} cy={y(points[dip.recovery_i].v)} r="4"
            className="ik-journey-dot is-rec" />
        )}
        <circle cx={x(shown.length - 1)} cy={y(shown[shown.length - 1].v)} r="4"
          className="ik-journey-head-dot" />
        </svg>

        {pastPeak && safeI < dip.recovery_i && (
          <Callout
            left={(x(dip.peak_i) / W) * 100}
            top={(y(points[dip.peak_i].v) / H) * 100}
            place="above"
            title="Your high point so far"
            value={money(val(dip.peak_i))}
            testid="intake-journey-callout-peak"
          />
        )}
        {atTrough && (
          <Callout
            left={(x(dip.trough_i) / W) * 100}
            top={(y(points[dip.trough_i].v) / H) * 100}
            place="below"
            tone="bad"
            title={`Down ${money(val(dip.peak_i) - val(dip.trough_i))} to`}
            value={money(val(dip.trough_i))}
            testid="intake-journey-callout-trough"
          />
        )}
        {safeI >= dip.recovery_i && (
          <Callout
            left={(x(dip.recovery_i) / W) * 100}
            top={(y(points[dip.recovery_i].v) / H) * 100}
            place="above"
            tone="good"
            title="Back above your high point"
            value={money(val(dip.recovery_i))}
            testid="intake-journey-callout-recovery"
          />
        )}
      </div>

      <div className="ik-journey-narr" key={ch} data-testid="intake-journey-narration">
        <div className="ik-journey-narr-title">{narr.title}</div>
        <p>{narr.body}</p>
      </div>

      {waiting && (
        <button type="button" className="ik-btn ik-btn-primary ik-journey-cta"
          onClick={() => { setWaiting(false); setCh(ch + 1); }}
          data-testid="intake-journey-continue">
          So what happens next?
        </button>
      )}

      {done && (
        <>
          <div className="ik-journey-close" data-testid="intake-journey-close">
            <p>
              <strong>Two things to bring to your attention.</strong> That dip was the worst of
              these ten years. Go back nearly two decades and the system has been down as much
              as <span className="ik-accent">{Math.abs(journey.full_record_maxdd_pct)}%</span>.
              To be conservative, plan for a drop of{' '}
              <span className="ik-accent">up to {Math.abs(journey.planning_dd_pct)}%</span>,
              because our record has never contained a dot-com-scale collapse.
            </p>
            <p>
              And we won't pretend every dip is already behind us. One is open right now:{' '}
              {Math.abs(journey.open_dip.current_pct)}% below the peak as of today. It may
              recover like the others did. It hasn't yet.
            </p>
            <p className="ik-quiet">
              You won't be left to work this out alone. When a stretch like the one you just
              watched arrives, we check in and put the number in context for you.
            </p>
          </div>
          <button type="button" className="ik-btn-link ik-journey-replay" onClick={replay}
            data-testid="intake-journey-replay">
            Watch it again
          </button>
        </>
      )}

      <div className="ik-journey-foot">
        Real path of the system over the last {journey.years} years
        ({journey.window_start} to {journey.window_end}), scaled to your amount. It is what
        already happened, not a forecast, and not a promise.
      </div>
    </div>
  );
};

export default IntakeJourney;
